// Assets
import Grayscale0 from "../../assets/images/grayscale/0.png";
import Grayscale1to5 from "../../assets/images/grayscale/1-5.png";
import Grayscale6to10 from "../../assets/images/grayscale/6-10.png";
import Grayscale11to15 from "../../assets/images/grayscale/11-15.png";
import Grayscale16to20 from "../../assets/images/grayscale/16-20.png";
import Grayscale21to25 from "../../assets/images/grayscale/21-25.png";
import Grayscale26to30 from "../../assets/images/grayscale/26-30.png";
import Grayscale31to35 from "../../assets/images/grayscale/31-35.png";
import Grayscale36to40 from "../../assets/images/grayscale/36-40.png";
import Grayscale41to50 from "../../assets/images/grayscale/41-50.png";
import Undefined from "../../assets/images/grayscale/undefined.png";
import LessThan05 from "../../assets/images/deviation-patterns/LessThan05.png";
import LessThan1 from "../../assets/images/deviation-patterns/LessThan1.png";
import LessThan2 from "../../assets/images/deviation-patterns/LessThan2.png";
import LessThan5 from "../../assets/images/deviation-patterns/LessThan5.png";

// Constants
import {
  examCardEnums,
  ExamCardSectionType,
  ExamCardSectionNameType,
  gridTypeEnums,
  eyeTypeEnums,
  VISUAL_FIELD_TYPE,
  positionTypeEnums,
  DEFAULT_GAZE_SPECS,
  PROGRESSION_CHART_SHAPES,
  algorithmTypes,
  examSourceEnums,
  OcularMotilityPositionLayoutType,
} from "src/constants/enums";

// Utils
import { getDecimalNum } from "../number/numberHelper";
import { calculateAgeFromExamDate, getLocalDate } from "../date/dateHelper";
import { isVrfiNotApplicable } from "./progressionAnalysisHelper";

// Types
import {
  AddExamSectionType,
  ChartSpotType,
  EnrichedExternalExamType,
  EnrichedRetinaLogikSectionType,
  ExamSectionReportType,
  ExamSectionType,
  ExamType,
  SelectedProgressionExamType,
} from "src/services/exams/types";
import { ClinicPackageExamType, ClinicType } from "src/services/clinics/types";

export const getReportExamsSortedByGroupId = (
  exam: ExamType
): Array<ExamSectionType & { sectionName: ExamCardSectionNameType }> => {
  const examSectionKeys = Object.keys(exam).filter((key) =>
    key.includes("Sections")
  ) as Array<ExamCardSectionNameType>;

  return examSectionKeys
    ?.map((sectionName) =>
      exam[sectionName]?.map((examSection) => ({
        ...examSection,
        sectionName, // add sectionName to data
      }))
    )
    ?.flatMap((value) => (value ? [value] : [])) // remove undefined values
    ?.flat()
    ?.sort((a, b) => {
      // sort by ascending groupId first
      if (a?.groupId !== b?.groupId) {
        return (a?.groupId ?? 0) - (b?.groupId ?? 0);
      }

      // sort by ascending section order next
      return a?.order - b?.order;
    });
};

export const getIncompleteExamsSortedByGroupId = (
  exam:
    | ClinicPackageExamType
    | {
        [key in ExamCardSectionNameType]: Array<ExamSectionType>;
      }
): Array<AddExamSectionType & { sectionName: ExamCardSectionNameType }> => {
  const examSectionKeys = Object.keys(exam).filter((key) =>
    key.includes("Sections")
  ) as Array<ExamCardSectionNameType>;

  return examSectionKeys
    ?.map((sectionName) =>
      exam[sectionName]?.map((examSection) => ({
        ...examSection,
        sectionName, // add sectionName to data
      }))
    )
    ?.flatMap((value) => (value ? [value] : [])) // remove undefined values
    ?.flat()
    ?.sort((a, b) => (b?.groupId ?? 0) - (a?.groupId ?? 0)); // sort the largest id first
};

export const isMultiExamPackage = (exam: ExamType | ClinicPackageExamType) => {
  const examSectionKeys = Object.keys(exam).filter((key) =>
    key.includes("Sections")
  ) as Array<ExamCardSectionNameType>;

  if (
    examSectionKeys?.length > 1 &&
    examSectionKeys.every((section) => exam?.[section]?.length)
  ) {
    return true;
  }

  // logic to check if there are multiple group ids for exams of the same type
  const examSection: Array<AddExamSectionType> =
    exam[examSectionKeys?.[0]] ?? [];
  const groupIds = examSection?.map((examKey) => examKey.groupId);
  const hasMultipleGroupIds = new Set(groupIds).size > 1;

  return Boolean(examSectionKeys?.length === 1 && hasMultipleGroupIds);
};

export const getVisualField = (
  examGridType: keyof typeof gridTypeEnums | undefined
) => {
  if (examGridType && gridTypeEnums[examGridType])
    return gridTypeEnums[examGridType].label;

  return "";
};

export const getExamGridType = (
  examName: ExamCardSectionType,
  examGrid: keyof typeof gridTypeEnums | undefined
) => {
  const examGridType = getVisualField(examGrid);

  // do not display grid type for undefined grids or esterman exams
  if (
    !examGridType ||
    examGridType === "" ||
    examName === examCardEnums.VISUAL_FIELD_ESTERMAN.type ||
    examName === examCardEnums.VISUAL_FIELD_SUPERIOR_36.type
  ) {
    return "";
  }

  return ` | ${examGridType}`;
};

export const getExamText = (
  exam: { [key in ExamCardSectionNameType]: Array<ExamSectionType> },
  t: (input: string) => string
) => {
  let examText = "";

  const enumValues = Object.values(examCardEnums);

  const sortedByGroupId = getIncompleteExamsSortedByGroupId(exam);
  const largestGroupId = sortedByGroupId?.[0]?.groupId ?? 0;
  const arrayLength = largestGroupId + 1;

  Array.from({ length: arrayLength }).forEach((_, index) => {
    const examSections = sortedByGroupId.filter(
      (section) => section.groupId === index || section.groupId == null
    );

    // get the current section info
    const enumValue = enumValues.find(
      (value) => value.sectionName === examSections?.[0]?.sectionName
    );

    if (!enumValue) return examText;
    if (enumValue.algorithmPath === examCardEnums.VIDEO.algorithmPath) {
      const videoName = examSections?.[0]?.videoName ?? "N/A";
      examText += `${t("Video")} | ${videoName}\n`;
      return examText;
    }

    const algorithm = examSections?.[0]?.algorithm;
    const gridType = examSections?.[0]?.gridType as keyof typeof gridTypeEnums;

    examText += `${t(enumValue?.translatedType)}  | ${t(algorithm)}`;
    examText += `${getExamGridType(enumValue?.type, gridType)}\n`;
  });

  return examText;
};

export const getEyeText = (
  exam: { [key in ExamCardSectionNameType]: Array<ExamSectionType> },
  t: (input: string) => string
) => {
  const { BOTH } = eyeTypeEnums;
  let eyeText = "";

  const sortedByGroupId = getIncompleteExamsSortedByGroupId(exam);
  const largestGroupId = sortedByGroupId?.[0]?.groupId ?? 0;
  const arrayLength = largestGroupId + 1;

  Array.from({ length: arrayLength }).forEach((_, index) => {
    const examSections = sortedByGroupId.filter(
      (section) => section.groupId === index || section.groupId == null
    );

    const firstSection = examSections?.[0];
    const isSimulation = firstSection?.algorithm?.includes("SIMULATION");

    // binocular
    if (
      examSections?.length === 1 &&
      (firstSection?.eye === BOTH || isSimulation)
    ) {
      eyeText += `${t("Binocular")}\n`;
    }
    // monocular multiple sections
    else if (Boolean(examSections?.length > 1)) {
      const firstEye = examSections?.find(
        (section) => section.order === 0
      )?.eye;
      const secondEye = examSections?.find(
        (section) => section.order === 1
      )?.eye;
      eyeText += `${t(`${firstEye} / ${secondEye} Eye`)}\n`;
    }
    // monocular single section
    else {
      eyeText += `${t(`${examSections?.[0]?.eye} Eye`)}\n`;
    }
  });

  return eyeText;
};

export const getAllExamSections = (exam: ExamType): Array<ExamSectionType> => {
  const sectionNames = Object.values(examCardEnums).map(
    (enumValue) => enumValue.sectionName
  );

  let examSection: Array<ExamSectionType> = [];
  sectionNames.forEach((sectionName) => {
    const sections = exam?.[sectionName];
    if (sections) {
      examSection.push(...sections);
    }
  });

  // return exam sections that exist
  // example: colorVisionSections
  return examSection;
};

// True when a simulation section was saved with no ids, which leaves nothing to run
export const hasEmptySimulation = (exam: ExamType): boolean => {
  // IOL and progressive simulations store their ids under lenses
  const isEmptyIOLSimulation =
    Boolean(exam?.iolLensSimulationSections) &&
    !exam?.iolLensSimulationSections?.[0]?.lenses?.length;
  const isEmptyProgressiveSimulation =
    Boolean(exam?.progressiveLensSimulationSections) &&
    !exam?.progressiveLensSimulationSections?.[0]?.lenses?.length;

  // dry eye simulations store their ids under profiles
  const isEmptyDryEyeSimulation =
    Boolean(exam?.dryEyeSimulationSections) &&
    !exam?.dryEyeSimulationSections?.[0]?.profiles?.length;

  return (
    isEmptyIOLSimulation ||
    isEmptyProgressiveSimulation ||
    isEmptyDryEyeSimulation
  );
};

export const getExamCardText = ({
  exam,
  t,
  isAbbrev = true,
}: {
  exam: ExamType;
  t: (input: string) => string;
  isAbbrev?: boolean;
}) => {
  if (isMultiExamPackage(exam)) {
    return t("Multi-Exam Package");
  }

  let examCardText = "";

  const examSectionKeys = Object.keys(exam).filter((key) =>
    key.includes("Sections")
  ) as Array<ExamCardSectionNameType>;

  const enumValues = Object.values(examCardEnums);

  examSectionKeys?.forEach((sectionName) => {
    const enumValue = enumValues.find(
      (value) => value.sectionName === sectionName
    );

    if (!enumValue) return examCardText;
    if (enumValue.algorithmPath === examCardEnums.VIDEO.algorithmPath) {
      const videoName = exam?.[sectionName]?.[0]?.videoName ?? "N/A";
      examCardText += `${t("Video")} | ${videoName}\n`;
      return examCardText;
    }

    const examSection = exam[sectionName]?.[0];

    if (isAbbrev) {
      examCardText += `${t(enumValue.abbrevType)} | ${t(examSection?.algorithm ?? "")}`;
    } else {
      examCardText += `${t(enumValue.translatedType)} | ${t(examSection?.algorithm ?? "")}`;
    }

    examCardText += `${getExamGridType(enumValue.type, examSection?.gridType)}\n`;
  });

  return examCardText;
};

export const getTooltipText = (
  exam: ExamType,
  t: (input: string) => string
) => {
  let tooltipText = "";

  const enumValues = Object.values(examCardEnums);

  const sortedByGroupId = getIncompleteExamsSortedByGroupId(exam);
  const largestGroupId = sortedByGroupId?.[0]?.groupId ?? 0;
  const arrayLength = largestGroupId + 1;

  Array.from({ length: arrayLength }).forEach((_, index) => {
    const examSections = sortedByGroupId.filter(
      (section) => section.groupId === index || section.groupId == null
    );

    // get the current section info
    const enumValue = enumValues.find(
      (value) => value.sectionName === examSections?.[0]?.sectionName
    );

    if (!enumValue) return tooltipText;
    if (enumValue.algorithmPath === examCardEnums.VIDEO.algorithmPath) {
      const videoName = examSections?.[0]?.videoName ?? "N/A";
      tooltipText += `${t("Video")} | ${videoName}\n`;
      return tooltipText;
    }

    const algorithm = examSections?.[0]?.algorithm;
    const gridType = examSections?.[0]?.gridType as keyof typeof gridTypeEnums;

    tooltipText += `${t(enumValue?.translatedType)}  | ${t(algorithm ?? "")}`;
    tooltipText += `${getExamGridType(enumValue.type, gridType)}\n`;
  });

  return tooltipText;
};

export const getCompletedExamsTooltip = (
  exam: ExamType,
  t: (input: string) => string
) => {
  let tooltipText = `${t("Exams Completed")}:\n`;

  const enumValues = Object.values(examCardEnums);

  const sortedByGroupId = getIncompleteExamsSortedByGroupId(exam);
  const largestGroupId = sortedByGroupId?.[0]?.groupId ?? 0;
  const arrayLength = largestGroupId + 1;

  Array.from({ length: arrayLength }).forEach((_, index) => {
    const examSections = sortedByGroupId.filter(
      (section) => section.groupId === index || section.groupId == null
    );

    // get the current section info
    const enumValue = enumValues.find(
      (value) => value.sectionName === examSections?.[0]?.sectionName
    );

    if (!enumValue) return tooltipText;

    const isVideoSection =
      enumValue.algorithmPath === examCardEnums.VIDEO.algorithmPath;
    const hasCompletedSection = exam?.[examSections?.[0]?.sectionName]?.some(
      (section) =>
        (section.groupId === index || section.groupId == null) &&
        Boolean(section.completionDate)
    );

    if (hasCompletedSection && isVideoSection) {
      const videoName = examSections?.[0]?.videoName ?? "N/A";
      tooltipText += `${t("Video")} | ${videoName}\n`;
    } else if (hasCompletedSection) {
      const algorithm = examSections?.[0]?.algorithm;
      const gridType = examSections?.[0]
        ?.gridType as keyof typeof gridTypeEnums;

      tooltipText += `${t(enumValue?.translatedType)}  | ${t(algorithm ?? "")}`;
      tooltipText += `${getExamGridType(enumValue?.type, gridType)}\n`;
    }
  });

  const completedSections = tooltipText.split("\n");
  if (completedSections.length === 2) {
    tooltipText += "N/A";
  }

  return tooltipText;
};

export const isBinocular = (examSections: Array<ExamSectionType>) => {
  const { BOTH } = eyeTypeEnums;
  return Boolean(
    examSections &&
      examSections?.length === 1 &&
      examSections?.[0]?.eye === BOTH
  );
};

export const isMonocularMultipleSections = (
  examSections: Array<ExamSectionType>
) => {
  return Boolean(examSections && examSections?.length > 1);
};

export const calculateDurationForDisplay = (elapsedTimeSeconds: number) => {
  if (elapsedTimeSeconds <= 0) return "00:00";
  // seconds
  let timeInSeconds = elapsedTimeSeconds;

  // days
  const days = Math.floor(timeInSeconds / 86400);
  timeInSeconds -= days * 86400;

  // hours
  const hours = Math.floor(timeInSeconds / 3600) % 24;
  timeInSeconds -= hours * 3600;

  //  minutes
  const minutes = Math.floor(timeInSeconds / 60) % 60;
  timeInSeconds -= minutes * 60;

  //  seconds
  const seconds = Math.floor(timeInSeconds % 60);

  // format a string with what's not 0
  var result = "";

  let remain = hours % 100;
  if (hours) result += (remain < 10 ? "0" + remain : remain) + ":";

  remain = minutes % 100;
  result += (remain < 10 ? "0" + remain : remain) + ":";

  remain = seconds % 100;
  result += remain < 10 ? "0" + remain : remain;

  return result;
};

export const isValidSynoptophoreReport = (report: ExamSectionReportType) => {
  const isDeviationAndCorrespondence =
    report?.exam?.algorithm === algorithmTypes.DEVIATION_AND_CORRESPONDENCE &&
    report?.state?.completedModules &&
    report?.state?.completedModules?.length > 0;

  const isFusionAndVergence =
    report?.exam?.algorithm === algorithmTypes.FUSION_AND_VERGENCE &&
    report?.state?.completedModules &&
    report?.state?.completedModules?.length > 0;

  const isStereopsis =
    report?.exam?.algorithm === algorithmTypes.STEREOPSIS &&
    report?.state?.completedModules &&
    report?.state?.completedModules?.length > 0;

  return isDeviationAndCorrespondence || isFusionAndVergence || isStereopsis;
};

// An aniseikonia report is worth displaying once the module has produced a summary. The module
// completing is not enough on its own: a section abandoned partway still carries a completed
// module list but no DirectComparison summary to read a headline off.
export const isValidAniseikoniaReport = (report: ExamSectionReportType) =>
  Boolean(
    report?.exam?.algorithm === algorithmTypes.DIRECT_COMPARISON &&
      report?.postProcessing?.summary?.DirectComparison
  );

export const isValidColorVisionReport = (report: ExamSectionReportType) => {
  const isD15 =
    report?.exam?.type === examCardEnums.COLOR_VISION.type &&
    report?.exam?.algorithm?.includes("D_15") &&
    report?.state?.row1 &&
    report?.state?.row2;

  const isIshihara =
    report?.exam?.type === examCardEnums.COLOR_VISION.type &&
    report?.exam?.algorithm?.includes("ISHIHARA") &&
    report?.state?.responses;

  return isD15 || isIshihara;
};

export const isValidRAPDReport = (report: ExamSectionReportType) => {
  return (
    report?.exam?.type === examCardEnums.RAPD.type &&
    report?.postProcessing?.pupilEventData
  );
};

// for contrast sensitivity tumbling e exams
export const isValidCSTumblingEReport = (report: ExamSectionReportType) => {
  return (
    report?.exam?.type === examCardEnums.CONTRAST_SENSITIVITY.type &&
    report?.state?.directions
  );
};

// for contrast sensitivity pelli robson exams
export const isValidPelliRobsonReport = (report: ExamSectionReportType) => {
  return (
    report?.exam?.type === examCardEnums.CONTRAST_SENSITIVITY.type &&
    report?.state?.letters
  );
};

// for all visual acuity exams (snellen, bailey lovie, etc)
export const isValidVisualAcuityReport = (report: ExamSectionReportType) => {
  return (
    report?.exam?.type === examCardEnums.VISUAL_ACUITY.type &&
    report?.state?.charts
  );
};

// for all visual field exams (esterman, full 120, superior 36, etc)
export const isValidVisualFieldReport = (report: ExamSectionReportType) => {
  return (
    report?.exam?.type?.includes(VISUAL_FIELD_TYPE) &&
    report?.state?.visualSpots
  );
};

export const isValidOcularMotilityReport = (report: ExamSectionReportType) => {
  return (
    report?.exam?.type === examCardEnums.OCULAR_MOTILITY.type &&
    (report?.postProcessing?.hPatternResults ||
      report?.postProcessing?.nineGazeResults)
  );
};

export const isValidLensSimulationReport = (report: ExamSectionReportType) => {
  return (
    report?.exam?.type?.includes("LensSimulation") && report?.state?.lenses
  );
};

export const isValidVideoReport = (report: ExamSectionReportType) => {
  return (
    report?.exam?.type === examCardEnums.VIDEO.type && report?.state?.videoData
  );
};

// return exam sections with a completion date
export const getCompletedExamSections = (exam: ExamType) => {
  const examSectionNames = Object.keys(exam).filter((key) =>
    key.includes("Sections")
  ) as Array<ExamCardSectionNameType>;

  return examSectionNames?.filter((key) => {
    const examSection = exam?.[key];
    return (
      examSection &&
      examSection?.length > 0 &&
      examSection?.some((examSection) => Boolean(examSection.completionDate))
    );
  });
};

// for multi-exam packages, return all exam sections with a completion date
export const getCompleteMultiExamSections = (exam: ExamType) => {
  const examSectionNames = Object.keys(exam).filter((key) =>
    key.includes("Sections")
  ) as Array<ExamCardSectionNameType>;

  const completedExamSections: Array<ExamSectionType> = [];

  examSectionNames?.forEach((sectionName) => {
    const examSections = exam?.[sectionName];
    const completedSections =
      examSections?.filter((section) => Boolean(section.completionDate)) ?? [];
    completedExamSections.push(...completedSections);
  });

  return completedExamSections;
};

export const getExamPackageText = (
  examSection: ExamSectionType | undefined,
  t: (input: string) => string
) => {
  if (!examSection) {
    return "";
  }

  let currentExamText = "";

  const enumValues = Object.values(examCardEnums);
  const enumValue = enumValues.find(
    (value) => value.type === examSection?.type
  );

  if (!enumValue) return currentExamText;
  if (enumValue.algorithmPath === examCardEnums.VIDEO.algorithmPath) {
    const videoName = examSection?.videoName ?? "N/A";
    currentExamText += `${t("Video")} | ${videoName}\n`;
    return currentExamText;
  }

  currentExamText += `${t(enumValue?.translatedType)} | ${t(examSection?.algorithm ?? "")}`;
  currentExamText += `${getExamGridType(examSection?.type, examSection?.gridType)}`;

  if (
    examSection?.position === positionTypeEnums.NEAR ||
    examSection?.position === positionTypeEnums.FAR
  ) {
    currentExamText += ` | ${t(examSection?.position)}`;
  }

  return currentExamText;
};

export const getExamSectionEye = (
  examSection: ExamSectionType | undefined,
  t: (input: string) => string
) => {
  if (!examSection) {
    return "";
  }

  let currentExamText = "";

  const enumValues = Object.values(examCardEnums);
  const enumValue = enumValues.find(
    (value) => value.type === examSection?.type
  );

  if (!enumValue) return currentExamText;

  if (examSection?.eye === eyeTypeEnums.LEFT) {
    currentExamText = "OS";
  } else if (examSection?.eye === eyeTypeEnums.RIGHT) {
    currentExamText = "OD";
  } else {
    currentExamText = "OU";
  }

  return currentExamText;
};

export const getExamSectionText = (
  examSection: ExamSectionType | undefined,
  t: (input: string) => string
) => {
  if (!examSection) {
    return "";
  }

  let currentExamText = "";

  const enumValues = Object.values(examCardEnums);
  const enumValue = enumValues.find(
    (value) => value.type === examSection?.type
  );

  if (!enumValue) return currentExamText;
  if (enumValue.algorithmPath === examCardEnums.VIDEO.algorithmPath) {
    const videoName = examSection?.videoName ?? "N/A";
    currentExamText += `${t("Video")} | ${videoName}\n`;
    return currentExamText;
  }

  currentExamText += `${t(enumValue?.translatedType)} | ${t(examSection?.algorithm ?? "")}`;
  currentExamText += `${getExamGridType(examSection?.type, examSection?.gridType)}`;

  if (
    examSection?.position === positionTypeEnums.NEAR ||
    examSection?.position === positionTypeEnums.FAR
  ) {
    currentExamText += ` | ${t(examSection?.position)}`;
  }

  if (
    examSection?.eye === eyeTypeEnums.LEFT ||
    examSection?.eye === eyeTypeEnums.RIGHT
  ) {
    currentExamText += ` | ${t(`${examSection?.eye} Eye`)}`;
  }

  return currentExamText;
};

export const getTutorialCompletionText = (
  inputString: string | undefined,
  t: (input: string) => string
) => {
  switch (inputString) {
    case "Completed":
      return t("Completed");
    case "NotCompleted":
      return t("Not Completed");
    case "Skipped":
      return t("Skipped");
    default:
      return "N/A";
  }
};

export const getReportEyeText = (
  inputString: string,
  t: (input: string) => string
) => {
  const { RIGHT, BOTH } = eyeTypeEnums;

  switch (inputString) {
    case BOTH:
      return t("Binocular");
    case RIGHT:
      return t("Right");
    default:
      return t("Left");
  }
};

// Gets the VisualSpots data to populate the VisualField report charts.
export const getChartSpots = (
  examReport: ExamSectionReportType | undefined
) => {
  if (!examReport) return [];
  if (!examReport?.state?.visualSpots) return [];
  if (examReport?.state?.visualSpots.length === 0) return [];

  // formatting the spots into another structure, used by the number chart
  const formattedSpots = examReport?.state?.visualSpots?.map((spot) => ({
    decibelValue: Math.floor(spot.intensity),
    x: spot.position.x,
    y: spot.position.y,
    completed: spot.isCompleted,
    current: false,
    seen: spot.isSeen,
  }));

  // add the current visual spot into the formatted spots array
  const currentVisualSpot = examReport?.state?.currentVisualSpot;
  if (currentVisualSpot) {
    const currentVisualSpotData = {
      decibelValue: Math.floor(currentVisualSpot.intensity),
      x: currentVisualSpot.position.x,
      y: currentVisualSpot.position.y,
      completed: currentVisualSpot.isCompleted,
      current: true,
      seen: currentVisualSpot.isSeen ?? false,
    };
    formattedSpots.push(currentVisualSpotData);
  }

  return formattedSpots;
};

/**
 * Method to calculate the value to be displayed on the gaze chart, based on the angle value.
 * @param {*} angle The angle value, that will define the final value to be considered for the bar gaze chart on that specific position.
 * @param {*} chartSpecs The specs for a nivo bar chart.
 * @returns The gaze chart value representation for the given angle, and the distance from the bottom X axis it should have.
 */
export const calculateGazeChartPoint = (
  angle: number,
  chartSpecs: {
    maxValue: number;
    minValue: number;
    centralValue: number;
    totalValues: number;
  }
) => {
  // Some constants used to increase the intensity of the values in some cases.
  const negativeMultiplierConstant = 25;
  const posMultiplierConstant = 1.5;

  // The base nivo gaze chart specs, that is applied in case no specs is passed to calculateGazeChartPoint.
  const baseNivoGazechartSpecs = {
    maxValue: 100,
    minValue: -100,
    centralValue: 5,
    totalValues: 205,
  };

  // The specs to consider. We use the baseNivoGazeChartSpecs when no chartSpecs is informed.
  const specs = {
    ...baseNivoGazechartSpecs,
    ...chartSpecs,
  };

  // Method to get the bottom distance (the offset to place the black bar chart of the stack).
  const getBottomDistance = (angle: number, middlePosOffset: number) => {
    // We increase the intensity when angle === -1. We multiply by the negativeMultiplierConstant.
    // Angle === -1 means that the person blinked at that point.
    if (angle === -1)
      return middlePosOffset + angle * negativeMultiplierConstant;

    // By default, we also return the middlePosOffset.
    return middlePosOffset;
  };

  // Method to get the height of the black bar chart of the stack, that represents the patient's focus during the exam.
  const getGazeValue = (angle: number) => {
    // In case the angle is between 0 and 5, we don't show a spike (which means it should have the height of the centralValue defined on the specs).
    if (angle >= 0 && angle <= 5) return specs.centralValue;

    // When angle equals to -1, we need to increase the height according to the negativeMultiplierConstant.
    if (angle === -1)
      return specs.centralValue + Math.abs(angle) * negativeMultiplierConstant;

    // In this case, we simply add the angle value (multiplied by the defined posMultiplierConstant), to the centralValue defined on the specs.
    return specs.centralValue + angle * posMultiplierConstant;
  };

  // The vertical middle position, based on the max and min values from the chart specs.
  const middlePosOffset =
    (Math.abs(specs.maxValue) + Math.abs(specs.maxValue)) / 2;

  // Anything under 0 should be treated as if their eyes are closed (becomes -1).
  const adjustedAngle = angle < 0 ? -1 : angle;

  // The gaze chart value (the black bar part of the chart).
  // Should have the centralValue chart spec as height (for the bar section), and increase it in case the angle is greater than 4.
  const gazeValue = getGazeValue(adjustedAngle);

  // The bottom distance that the gaze chart shoud have, based on the angle value.
  const bottomDistance = getBottomDistance(adjustedAngle, middlePosOffset);

  // The chart will be composed of two stacked sections on a bar chart.
  // The top section (black bar, displayed on the vertical middle of the chart, because of the bottom section, used as offset) represents the visual part of the chart that will be built.
  // The bottom section (white) represents the distance to the bottom X axis, so that the middle section can be placed having the vertical middle of the chart as it's origin.
  return { gazeValue, bottomDistance };
};

export const getGazeChartData = (
  examReport: ExamSectionReportType | undefined,
  chartSpecs = DEFAULT_GAZE_SPECS
) => {
  let under0 = 0;
  let eyeTrackingError = null;

  if (!examReport) return { gazeSpots: [], eyeTrackingError };
  if (examReport?.misc?.eyeTracking === undefined)
    return { gazeSpots: [], eyeTrackingError };

  const gazeSpots = examReport?.misc?.eyeTracking.map((eyeData, id) => {
    if (eyeData < 0) under0++;
    const { gazeValue, bottomDistance } = calculateGazeChartPoint(
      eyeData,
      chartSpecs
    );

    return { id, gazeValue, bottomDistance };
  });

  eyeTrackingError = under0 * 2 >= examReport?.misc?.eyeTracking?.length;
  return { gazeSpots, eyeTrackingError };
};

export const isTerminatedExamReport = ({
  exam,
  examReport,
  sectionName,
}: {
  exam: ExamType | undefined;
  examReport: ExamSectionReportType;
  sectionName: ExamCardSectionNameType;
}) => {
  if (!exam?.[sectionName]?.length) return false;

  const { BOTH } = eyeTypeEnums;

  // Known issue: if a multi-exam package has 2+ exams with the same section type, eye, and grid
  // And one of these is terminated, the terminated status will be incorrectly applied to all
  const terminatedEyeSection = exam?.[sectionName]?.find(
    (section) =>
      (section.eye === examReport?.exam?.eye ||
        examReport?.exam?.eye === BOTH) &&
      section.gridType === examReport?.exam?.grid &&
      section.completionState === "Terminated"
  );

  return Boolean(terminatedEyeSection);
};

/**
 * Instantiate an array of arrays with the expected grid dimensions.
 * @param {*} gridSize The grid size.
 * @returns An array of size gridSize, with each index occupied by an array of gridSize.
 */
const initialGridDefinition = (gridSize: number) => {
  return new Array(gridSize)
    .fill(undefined)
    .map(() => new Array(gridSize).fill(undefined));
};

/**
 * Method to build the 24-2 type quadrant grid.
 * @param {*} points The values used to build the grid, having each X and Y position representing a points. These values will be the base of a new grid, with more sections.
 * @param {*} extended If it should extend the grid for this quadrant.
 * @returns An array of arrays, representing the grid of a quadrant.
 */
export const build24Dash2Grid = (
  points: Array<ChartSpotType>,
  extended = false
) => {
  const gridSize = 14; // grid dimension

  // Inject the initial values into the array of arrays (this varies per exam type).
  // We will consider the abs values for the X and Y position, and inject them on a new grid that has "gridSize" as dimension.
  // This way, the grid will be always built as if the values were on the top-right grid (with X and Y positives), then, the grid is mirrored according to how we want it to be displayed (according to the quadrant).
  const injectInitialValues = (
    array: Array<Array<number>>,
    points: Array<ChartSpotType>,
    extended: boolean
  ) => {
    // Mapping all values to a dictionary, for each X and Y pairs within the set os points received.
    const mappedPoints = points.reduce((acc: any, cur) => {
      const key = `valX${Math.round(Math.abs(cur.x))}Y${Math.round(Math.abs(cur.y))}`;

      if (acc[key])
        acc[key].decibelValue =
          (acc[key].decibelValue + cur.decibelValue) / 2.0;
      else acc[key] = cur;
      return acc;
    }, {});

    for (const point of Object.keys(mappedPoints)) {
      if (!mappedPoints[point]?.completed)
        mappedPoints[point].decibelValue = undefined;
    }

    // Values in which Y = 1 (in the original X and Y position values).
    array[0][0] = mappedPoints.valX1Y1?.decibelValue;
    array[0][1] = mappedPoints.valX1Y1?.decibelValue;
    array[1][0] = mappedPoints.valX1Y1?.decibelValue;
    array[1][1] = mappedPoints.valX1Y1?.decibelValue;

    array[0][3] = mappedPoints.valX2Y1?.decibelValue;
    array[0][4] = mappedPoints.valX2Y1?.decibelValue;
    array[1][3] = mappedPoints.valX2Y1?.decibelValue;
    array[1][4] = mappedPoints.valX2Y1?.decibelValue;

    array[0][6] = mappedPoints.valX3Y1?.decibelValue;
    array[0][7] = mappedPoints.valX3Y1?.decibelValue;
    array[1][6] = mappedPoints.valX3Y1?.decibelValue;
    array[1][7] = mappedPoints.valX3Y1?.decibelValue;

    array[0][9] = mappedPoints.valX4Y1?.decibelValue;
    array[0][10] = mappedPoints.valX4Y1?.decibelValue;
    array[1][9] = mappedPoints.valX4Y1?.decibelValue;
    array[1][10] = mappedPoints.valX4Y1?.decibelValue;

    if (extended) {
      array[0][12] = mappedPoints.valX5Y1?.decibelValue;
      array[0][13] = mappedPoints.valX5Y1?.decibelValue;
      array[1][12] = mappedPoints.valX5Y1?.decibelValue;
      array[1][13] = mappedPoints.valX5Y1?.decibelValue;
    }

    // Values in which Y = 2 (in the original X and Y position values).
    array[3][0] = mappedPoints.valX1Y2?.decibelValue;
    array[3][1] = mappedPoints.valX1Y2?.decibelValue;
    array[4][0] = mappedPoints.valX1Y2?.decibelValue;
    array[4][1] = mappedPoints.valX1Y2?.decibelValue;

    array[3][3] = mappedPoints.valX2Y2?.decibelValue;
    array[3][4] = mappedPoints.valX2Y2?.decibelValue;
    array[4][3] = mappedPoints.valX2Y2?.decibelValue;
    array[4][4] = mappedPoints.valX2Y2?.decibelValue;

    array[3][6] = mappedPoints.valX3Y2?.decibelValue;
    array[3][7] = mappedPoints.valX3Y2?.decibelValue;
    array[4][6] = mappedPoints.valX3Y2?.decibelValue;
    array[4][7] = mappedPoints.valX3Y2?.decibelValue;

    array[3][9] = mappedPoints.valX4Y2?.decibelValue;
    array[3][10] = mappedPoints.valX4Y2?.decibelValue;
    array[4][9] = mappedPoints.valX4Y2?.decibelValue;
    if (extended) {
      array[4][10] = mappedPoints.valX4Y2?.decibelValue;
    }

    // Values in which Y = 3 (in the original X and Y position values).
    array[6][0] = mappedPoints.valX1Y3?.decibelValue;
    array[6][1] = mappedPoints.valX1Y3?.decibelValue;
    array[7][0] = mappedPoints.valX1Y3?.decibelValue;
    array[7][1] = mappedPoints.valX1Y3?.decibelValue;

    array[6][3] = mappedPoints.valX2Y3?.decibelValue;
    array[6][4] = mappedPoints.valX2Y3?.decibelValue;
    array[7][3] = mappedPoints.valX2Y3?.decibelValue;
    array[7][4] = mappedPoints.valX2Y3?.decibelValue;

    array[6][6] = mappedPoints.valX3Y3?.decibelValue;
    array[6][7] = mappedPoints.valX3Y3?.decibelValue;
    array[7][6] = mappedPoints.valX3Y3?.decibelValue;
    array[7][7] = mappedPoints.valX3Y3?.decibelValue;

    // Values in which Y = 4 (in the original X and Y position values).
    array[9][0] = mappedPoints.valX1Y4?.decibelValue;
    array[9][1] = mappedPoints.valX1Y4?.decibelValue;
    array[10][0] = mappedPoints.valX1Y4?.decibelValue;
    array[10][1] = mappedPoints.valX1Y4?.decibelValue;

    array[9][3] = mappedPoints.valX2Y4?.decibelValue;
    array[9][4] = mappedPoints.valX2Y4?.decibelValue;
    array[10][3] = mappedPoints.valX2Y4?.decibelValue;

    return array;
  };

  // Inject the final values into the array of arrays (this varies per exam type).
  const injectFinalValues = (
    array: Array<Array<number>>,
    extended: boolean
  ) => {
    // Method that will "merge" the initial values with their horizontal undefined values.
    const mergeInitialValuesHorizontally = () => {
      // Values in which Y = 0 or Y = 1.
      array[0][2] = 0.5 * array[0][1] + 0.5 * array[0][3];
      array[1][2] = 0.5 * array[1][1] + 0.5 * array[1][3];
      array[0][5] = 0.5 * array[0][4] + 0.5 * array[0][6];
      array[1][5] = 0.5 * array[1][4] + 0.5 * array[1][6];
      array[0][8] = 0.5 * array[0][7] + 0.5 * array[0][9];
      array[1][8] = 0.5 * array[1][7] + 0.5 * array[1][9];
      array[0][11] = 0.5 * array[0][10] + 0.5 * array[0][12];
      array[1][11] = 0.5 * array[1][10] + 0.5 * array[1][12];

      // Values in which Y = 3 or Y = 4.
      array[3][2] = 0.5 * array[3][1] + 0.5 * array[3][3];
      array[4][2] = 0.5 * array[4][1] + 0.5 * array[4][3];
      array[3][5] = 0.5 * array[3][4] + 0.5 * array[3][6];
      array[4][5] = 0.5 * array[4][4] + 0.5 * array[4][6];
      array[3][8] = 0.5 * array[3][7] + 0.5 * array[3][9];
      array[4][8] = 0.5 * array[4][7] + 0.5 * array[4][9];
      if (extended) {
        array[3][11] = array[3][10];
      }

      // Values in which Y = 6 or Y = 7.
      array[6][2] = 0.5 * array[6][1] + 0.5 * array[6][3];
      array[7][2] = 0.5 * array[7][1] + 0.5 * array[7][3];
      array[6][5] = 0.5 * array[6][4] + 0.5 * array[6][6];
      array[7][5] = 0.5 * array[7][4] + 0.5 * array[7][6];
      array[6][8] = array[6][7];

      // Values in which Y = 9 or Y = 10.
      array[9][2] = 0.5 * array[9][1] + 0.5 * array[9][3];
      array[10][2] = 0.5 * array[10][1] + 0.5 * array[10][3];
      array[9][5] = array[9][4];
    };

    // Method that will "merge" the initial values on their vertical undefined values.
    const mergeInitialValuesVertically = () => {
      // Values in which X = 0 or X = 1.
      array[2][0] = 0.5 * array[1][0] + 0.5 * array[3][0];
      array[2][1] = 0.5 * array[1][1] + 0.5 * array[3][1];
      array[5][0] = 0.5 * array[4][0] + 0.5 * array[6][0];
      array[5][1] = 0.5 * array[4][1] + 0.5 * array[6][1];
      array[8][0] = 0.5 * array[7][0] + 0.5 * array[9][0];
      array[8][1] = 0.5 * array[7][1] + 0.5 * array[9][1];

      // Values in which X = 3 or X = 4.
      array[2][3] = 0.5 * array[1][3] + 0.5 * array[3][3];
      array[2][4] = 0.5 * array[1][4] + 0.5 * array[3][4];
      array[5][3] = 0.5 * array[4][3] + 0.5 * array[6][3];
      array[5][4] = 0.5 * array[4][4] + 0.5 * array[6][4];
      array[8][3] = 0.5 * array[7][3] + 0.5 * array[9][3];
      array[8][4] = 0.5 * array[7][4] + 0.5 * array[9][4];

      // Values in which X = 6 or X = 7.
      array[2][6] = 0.5 * array[1][6] + 0.5 * array[3][6];
      array[2][7] = 0.5 * array[1][7] + 0.5 * array[3][7];
      array[5][6] = 0.5 * array[4][6] + 0.5 * array[6][6];
      array[5][7] = 0.5 * array[4][7] + 0.5 * array[6][7];
      array[8][6] = array[7][6];

      // Values in which X = 9 or X = 10.
      array[2][9] = 0.5 * array[1][9] + 0.5 * array[3][9];
      array[2][10] = 0.5 * array[1][10] + 0.5 * array[3][10];
      array[5][9] = array[4][9];

      // Values in which X = 12.
      if (extended) {
        array[2][12] = array[1][12];
      }
    };

    // Fill the inner values that are not yet defined, based on the top-right-bottom-left neighbours. This depends on initialGridDefinition, injectInitialValues, mergeInitialValuesHorizontally and mergeInitialValuesVertically.
    // Method that will "merge"/"blend" the inner gap values, to be a representation of its neighbours (top-right-bottom-left).
    const mergeInnerGaps = () => {
      // Calculates the new value based on the top-right-bottom-left neighbour values.
      const calculateBasedOnDirectNeighbours = (
        array: Array<Array<number>>,
        x: number,
        y: number
      ) => {
        return (
          0.25 * array[y - 1][x] +
          0.25 * array[y + 1][x] +
          0.25 * array[y][x - 1] +
          0.25 * array[y][x + 1]
        );
      };

      // Values in which Y = 2.
      array[2][2] = calculateBasedOnDirectNeighbours(array, 2, 2);
      array[2][5] = calculateBasedOnDirectNeighbours(array, 5, 2);
      array[2][8] = calculateBasedOnDirectNeighbours(array, 8, 2);
      if (extended) {
        array[2][11] = calculateBasedOnDirectNeighbours(array, 11, 2);
      }

      // Values in which Y = 5.
      array[5][2] = calculateBasedOnDirectNeighbours(array, 2, 5);
      array[5][5] = calculateBasedOnDirectNeighbours(array, 5, 5);
      array[5][8] = calculateBasedOnDirectNeighbours(array, 8, 5);

      // Values in which Y = 8.
      array[8][2] = calculateBasedOnDirectNeighbours(array, 2, 8);
      array[8][5] = calculateBasedOnDirectNeighbours(array, 5, 8);
    };

    // Update the values on the array (grid).
    mergeInitialValuesHorizontally();
    mergeInitialValuesVertically();
    mergeInnerGaps();

    return array;
  };

  // We first build a grid filled with undefined values.
  const rawGrid = initialGridDefinition(gridSize);
  // We then inject the initial values, based on the decibel values for the given quadrant points.
  const initialGrid = injectInitialValues(rawGrid, points, extended);
  // We then inject the final values, which are besed on the definition of the initial values.
  const finalGrid = injectFinalValues(initialGrid, extended);

  // We reverse it, so the grid system used to build it matches the axis point definitions on the pressedPoints.
  // For example: the 1,1 position on the grid would be place on a row at the top of the grid; but the 1,1 position on the axis world would coordinate the bottom; so we reverse the grid, so that it matches the axis world when displayed.
  return finalGrid.reverse();
};

/**
 * Method to build the 30-2 type quadrant grid.
 * @param {*} points The values used to build the grid, having each X and Y position representing a points. These values will be the base of a new grid, with more sections.
 * @returns An array of arrays, representing the grid of a quadrant.
 */
export const build30Dash2Grid = (points: Array<ChartSpotType>) => {
  const gridSize = 14; // grid dimension

  // Inject the initial values into the array of arrays (this varies per exam type).
  // We will consider the abs values for the X and Y position, and inject them on a new grid that has "gridSize" as dimension.
  // This way, the grid will be always built as if the values were on the top-right grid (with X and Y positives), then, the grid is mirrored according to how we want it to be displayed (according to the quadrant).
  const injectInitialValues = (
    array: Array<Array<number>>,
    points: Array<ChartSpotType>
  ) => {
    // Mapping all values to a dictionary, for each X and Y pairs within the set os points received.
    const mappedPoints = points.reduce(
      (acc: any, cur) => ({
        ...acc,
        [`valX${Math.abs(cur.x)}Y${Math.abs(cur.y)}`]: cur,
      }),
      {}
    );

    for (const point of Object.keys(mappedPoints)) {
      if (!mappedPoints[point]?.completed)
        mappedPoints[point].decibelValue = undefined;
    }

    // Values in which Y = 1 (in the original X and Y position values).
    array[0][0] = mappedPoints.valX1Y1?.decibelValue;
    array[0][1] = mappedPoints.valX1Y1?.decibelValue;
    array[1][0] = mappedPoints.valX1Y1?.decibelValue;
    array[1][1] = mappedPoints.valX1Y1?.decibelValue;

    array[0][4] = mappedPoints.valX2Y1?.decibelValue;
    array[1][4] = mappedPoints.valX2Y1?.decibelValue;

    array[0][7] = mappedPoints.valX3Y1?.decibelValue;
    array[1][7] = mappedPoints.valX3Y1?.decibelValue;

    array[0][10] = mappedPoints.valX4Y1?.decibelValue;
    array[1][10] = mappedPoints.valX4Y1?.decibelValue;

    array[0][13] = mappedPoints.valX5Y1?.decibelValue;
    array[1][13] = mappedPoints.valX5Y1?.decibelValue;

    // Values in which Y = 2 (in the original X and Y position values).
    array[4][0] = mappedPoints.valX1Y2?.decibelValue;
    array[4][1] = mappedPoints.valX1Y2?.decibelValue;

    array[4][4] = mappedPoints.valX2Y2?.decibelValue;

    array[4][7] = mappedPoints.valX3Y2?.decibelValue;

    array[4][10] = mappedPoints.valX4Y2?.decibelValue;

    array[4][13] = mappedPoints.valX5Y2?.decibelValue;

    // Values in which Y = 3 (in the original X and Y position values).
    array[7][0] = mappedPoints.valX1Y3?.decibelValue;
    array[7][1] = mappedPoints.valX1Y3?.decibelValue;

    array[7][4] = mappedPoints.valX2Y3?.decibelValue;

    array[7][8] = mappedPoints.valX3Y3?.decibelValue;

    array[7][11] = mappedPoints.valX4Y3?.decibelValue;

    // Values in which Y = 4 (in the original X and Y position values).
    array[10][0] = mappedPoints.valX1Y4?.decibelValue;
    array[10][1] = mappedPoints.valX1Y4?.decibelValue;

    array[10][4] = mappedPoints.valX2Y4?.decibelValue;

    array[10][8] = mappedPoints.valX3Y4?.decibelValue;

    // Values in which Y = 5 (in the original X and Y position values).
    array[13][0] = mappedPoints.valX1Y5?.decibelValue;
    array[13][1] = mappedPoints.valX1Y5?.decibelValue;

    array[13][4] = mappedPoints.valX2Y5?.decibelValue;

    return array;
  };

  // Inject the final values into the array of arrays (this varies per exam type).
  const injectFinalValues = (array: Array<Array<number>>) => {
    // Method that will "merge" the initial values with their horizontal undefined values.
    const mergeInitialValuesHorizontally = () => {
      // Values in which Y = 0 or Y = 1.
      array[0][2] = 0.7 * array[0][1] + 0.3 * array[0][4];
      array[1][2] = 0.7 * array[1][1] + 0.3 * array[1][4];
      array[0][3] = 0.3 * array[0][1] + 0.7 * array[0][4];
      array[1][3] = 0.3 * array[1][1] + 0.7 * array[1][4];

      array[0][5] = 0.7 * array[0][4] + 0.3 * array[0][7];
      array[1][5] = 0.7 * array[1][4] + 0.3 * array[1][7];
      array[0][6] = 0.3 * array[0][4] + 0.7 * array[0][7];
      array[1][6] = 0.3 * array[1][4] + 0.7 * array[1][7];

      array[0][8] = 0.7 * array[0][7] + 0.3 * array[0][10];
      array[1][8] = 0.7 * array[1][7] + 0.3 * array[1][10];
      array[0][9] = 0.3 * array[0][7] + 0.7 * array[0][10];
      array[1][9] = 0.3 * array[1][7] + 0.7 * array[1][10];

      array[0][11] = 0.7 * array[0][10] + 0.3 * array[0][13];
      array[1][11] = 0.7 * array[1][10] + 0.3 * array[1][13];
      array[0][12] = 0.3 * array[0][10] + 0.7 * array[0][13];
      array[1][12] = 0.3 * array[1][10] + 0.7 * array[1][13];

      // Values in which Y = 4.
      array[4][2] = 0.7 * array[4][1] + 0.3 * array[4][4];
      array[4][3] = 0.3 * array[4][1] + 0.7 * array[4][4];

      array[4][5] = 0.7 * array[4][4] + 0.3 * array[4][7];
      array[4][6] = 0.3 * array[4][1] + 0.7 * array[4][4];

      array[4][8] = 0.7 * array[4][7] + 0.3 * array[4][10];
      array[4][9] = 0.3 * array[4][7] + 0.7 * array[4][10];

      array[4][11] = 0.7 * array[4][10] + 0.3 * array[4][13];
      array[4][12] = 0.3 * array[4][10] + 0.7 * array[4][13];

      // Values in which Y = 7.
      array[7][2] = 0.7 * array[7][1] + 0.3 * array[7][4];
      array[7][3] = 0.3 * array[7][1] + 0.7 * array[7][4];

      array[7][5] = 0.7 * array[7][4] + 0.3 * array[7][8];
      array[7][6] = 0.5 * array[7][4] + 0.5 * array[7][8];
      array[7][7] = 0.3 * array[7][4] + 0.7 * array[7][8];

      array[7][9] = 0.7 * array[7][8] + 0.3 * array[7][11];
      array[7][10] = 0.3 * array[7][8] + 0.7 * array[7][11];

      // Values in which Y = 10.
      array[10][2] = 0.7 * array[10][1] + 0.3 * array[10][4];
      array[10][3] = 0.3 * array[10][1] + 0.7 * array[10][4];

      array[10][5] = 0.7 * array[10][4] + 0.3 * array[10][8];
      array[10][6] = 0.5 * array[10][4] + 0.5 * array[10][8];
      array[10][7] = 0.3 * array[10][4] + 0.7 * array[10][8];

      // Values in which Y = 13.
      array[13][2] = 0.7 * array[13][1] + 0.3 * array[13][4];
      array[13][3] = 0.3 * array[13][1] + 0.7 * array[13][4];
    };

    // Method that will "merge" the initial values on their vertical undefined values.
    const mergeInitialValuesVertically = () => {
      // Values in which X = 0 or X = 1.
      array[2][0] = 0.7 * array[1][0] + 0.3 * array[4][0];
      array[2][1] = 0.7 * array[1][1] + 0.3 * array[4][1];
      array[3][0] = 0.3 * array[1][0] + 0.7 * array[4][0];
      array[3][1] = 0.3 * array[1][1] + 0.7 * array[4][1];

      array[5][0] = 0.7 * array[4][0] + 0.3 * array[7][0];
      array[5][1] = 0.7 * array[4][1] + 0.3 * array[7][1];
      array[6][0] = 0.3 * array[4][0] + 0.7 * array[7][0];
      array[6][1] = 0.3 * array[4][1] + 0.7 * array[7][1];

      array[8][0] = 0.7 * array[7][0] + 0.3 * array[10][0];
      array[8][1] = 0.7 * array[7][1] + 0.3 * array[10][1];
      array[9][0] = 0.3 * array[7][0] + 0.7 * array[10][0];
      array[9][1] = 0.3 * array[7][1] + 0.7 * array[10][1];

      array[11][0] = 0.7 * array[10][0] + 0.3 * array[13][0];
      array[11][1] = 0.7 * array[10][1] + 0.3 * array[13][1];
      array[12][0] = 0.3 * array[10][0] + 0.7 * array[13][0];
      array[12][1] = 0.3 * array[10][1] + 0.7 * array[13][1];

      // Values in which X = 4.
      array[2][4] = 0.7 * array[1][4] + 0.3 * array[4][4];
      array[3][4] = 0.3 * array[1][4] + 0.7 * array[4][4];

      array[5][4] = 0.7 * array[4][4] + 0.3 * array[7][4];
      array[6][4] = 0.3 * array[4][4] + 0.7 * array[7][4];

      array[8][4] = 0.7 * array[7][4] + 0.3 * array[10][4];
      array[9][4] = 0.3 * array[7][4] + 0.7 * array[10][4];

      array[11][4] = 0.7 * array[10][4] + 0.3 * array[13][4];
      array[12][4] = 0.3 * array[10][4] + 0.7 * array[13][4];

      // Values in which X = 7.
      array[2][7] = 0.7 * array[1][7] + 0.3 * array[4][7];
      array[3][7] = 0.3 * array[1][7] + 0.7 * array[4][7];

      // Values in which X = 8.
      array[8][8] = 0.7 * array[7][8] + 0.3 * array[10][8];
      array[9][8] = 0.3 * array[7][8] + 0.7 * array[10][8];

      // Values in which X = 10.
      array[2][10] = 0.7 * array[1][10] + 0.3 * array[4][10];
      array[3][10] = 0.3 * array[1][10] + 0.7 * array[4][10];

      // Values in which X = 13.
      array[2][13] = 0.7 * array[1][13] + 0.3 * array[4][13];
      array[3][13] = 0.3 * array[1][13] + 0.7 * array[4][13];
    };

    // Fill the inner values that are not yet defined. This depends on initialGridDefinition, injectInitialValues, mergeInitialValuesHorizontally and mergeInitialValuesVertically.
    // Method that will "merge"/"blend" the inner gap values, to be a representation of its neighbours (top-bottom-left).
    const mergeInnerGaps = () => {
      // Values in which Y = 2 or Y = 3.
      array[2][2] = 0.7 * array[1][2] + 0.3 * array[4][2];
      array[2][3] = 0.7 * array[1][3] + 0.3 * array[4][3];
      array[3][2] = 0.3 * array[1][2] + 0.7 * array[4][2];
      array[3][3] = 0.3 * array[1][3] + 0.7 * array[4][3];

      array[2][5] = 0.7 * array[1][5] + 0.3 * array[4][5];
      array[2][6] = 0.7 * array[1][6] + 0.3 * array[4][6];
      array[3][5] = 0.3 * array[1][5] + 0.7 * array[4][5];
      array[3][6] = 0.3 * array[1][6] + 0.7 * array[4][6];

      array[2][8] = 0.7 * array[1][8] + 0.3 * array[4][8];
      array[2][9] = 0.7 * array[1][9] + 0.3 * array[4][9];
      array[3][8] = 0.3 * array[1][8] + 0.7 * array[4][8];
      array[3][9] = 0.3 * array[1][9] + 0.7 * array[4][9];

      array[2][11] = 0.7 * array[1][11] + 0.3 * array[4][11];
      array[2][12] = 0.7 * array[1][12] + 0.3 * array[4][12];
      array[3][11] = 0.3 * array[1][11] + 0.7 * array[4][11];
      array[3][12] = 0.3 * array[1][12] + 0.7 * array[4][12];

      // // Values in which Y = 5 or Y = 6.
      array[5][2] = 0.7 * array[4][2] + 0.3 * array[7][2];
      array[5][3] = 0.7 * array[4][3] + 0.3 * array[7][3];
      array[6][2] = 0.3 * array[4][2] + 0.7 * array[7][2];
      array[6][3] = 0.3 * array[4][3] + 0.7 * array[7][3];

      array[5][5] = 0.7 * array[4][5] + 0.3 * array[7][5];
      array[5][6] = 0.7 * array[4][6] + 0.3 * array[7][6];
      array[5][7] = 0.7 * array[4][7] + 0.3 * array[7][7];
      array[5][8] = 0.7 * array[4][8] + 0.3 * array[7][8];
      array[5][9] = 0.7 * array[4][9] + 0.3 * array[7][9];
      array[5][10] = 0.7 * array[4][10] + 0.3 * array[7][10];
      array[5][11] = 0.7 * array[4][11] + 0.3 * array[7][11];

      array[6][5] = 0.3 * array[4][5] + 0.7 * array[7][5];
      array[6][6] = 0.3 * array[4][6] + 0.7 * array[7][6];
      array[6][7] = 0.3 * array[4][7] + 0.7 * array[7][7];
      array[6][8] = 0.3 * array[4][8] + 0.7 * array[7][8];
      array[6][9] = 0.3 * array[4][9] + 0.7 * array[7][9];
      array[6][10] = 0.3 * array[4][10] + 0.7 * array[7][10];
      array[6][11] = 0.3 * array[4][11] + 0.7 * array[7][11];

      // Values in which Y = 8 or Y = 9.
      array[8][2] = 0.7 * array[7][2] + 0.3 * array[10][2];
      array[8][3] = 0.7 * array[7][3] + 0.3 * array[10][3];
      array[9][2] = 0.3 * array[7][2] + 0.7 * array[10][2];
      array[9][3] = 0.3 * array[7][3] + 0.7 * array[10][3];

      array[8][5] = 0.7 * array[7][5] + 0.3 * array[10][5];
      array[8][6] = 0.7 * array[7][6] + 0.3 * array[10][6];
      array[8][7] = 0.7 * array[7][7] + 0.3 * array[10][7];
      array[9][5] = 0.3 * array[7][5] + 0.7 * array[10][5];
      array[9][6] = 0.3 * array[7][6] + 0.7 * array[10][6];
      array[9][7] = 0.3 * array[7][7] + 0.7 * array[10][7];

      array[8][9] = 0.7 * array[7][9] + 0.3 * array[10][9];
      array[9][9] = 0.3 * array[7][9] + 0.7 * array[10][9];

      // // Values in which Y = 11 or Y = 12.
      array[11][2] = 0.7 * array[10][2] + 0.3 * array[13][2];
      array[11][3] = 0.7 * array[10][3] + 0.3 * array[13][3];
      array[12][2] = 0.3 * array[10][2] + 0.7 * array[13][2];
      array[12][3] = 0.3 * array[10][3] + 0.7 * array[13][3];

      array[11][5] = array[11][4];
      array[12][5] = array[12][4];

      array[11][6] = array[11][5];
      array[12][6] = array[12][5];

      array[11][7] = array[10][7];
      array[11][8] = array[10][8];

      // Values in which Y = 10.
      array[10][9] = array[10][8];

      // Values in which Y = 8 or Y = 9.
      array[9][9] = 0.7 * array[10][9] + 0.3 * array[7][9];
      array[8][9] = 0.3 * array[10][9] + 0.7 * array[7][9];

      array[9][10] = array[9][9];
      array[8][10] = array[8][9];

      array[8][11] = array[7][11];

      // Values in which Y = 5 or Y = 6.
      array[6][12] = array[6][11];
      array[5][12] = array[5][11];
    };

    // Update the values on the array (grid).
    mergeInitialValuesHorizontally();
    mergeInitialValuesVertically();
    mergeInnerGaps();

    return array;
  };

  // We first build a grid filled with undefined values.
  const rawGrid = initialGridDefinition(gridSize);
  // We then inject the initial values, based on the decibel values for the given quadrant points.
  const initialGrid = injectInitialValues(rawGrid, points);
  // // We then inject the final values, which are besed on the definition of the initial values.
  const finalGrid = injectFinalValues(initialGrid);

  // We reverse it, so the grid system used to build it matches the axis point definitions on the pressedPoints.
  // For example: the 1,1 position on the grid would be place on a row at the top of the grid; but the 1,1 position on the axis world would coordinate the bottom; so we reverse the grid, so that it matches the axis world when displayed.
  return finalGrid.reverse();
};

/**
 * Method to build the 10-2 type quadrant grid.
 * @param {*} points The values used to build the grid, having each X and Y position representing a points. These values will be the base of a new grid, with more sections.
 * @returns An array of arrays, representing the grid of a quadrant.
 */
export const build10Dash2Grid = (points: Array<ChartSpotType>) => {
  const gridSize = 14; // grid dimension

  // Inject the initial values into the array of arrays (this varies per exam type).
  // We will consider the abs values for the X and Y position, and inject them on a new grid that has "gridSize" as dimension.
  // This way, the grid will be always built as if the values were on the top-right grid (with X and Y positives), then, the grid is mirrored according to how we want it to be displayed (according to the quadrant).
  const injectInitialValues = (
    array: Array<Array<number>>,
    points: Array<ChartSpotType>
  ) => {
    // Mapping all values to a dictionary, for each X and Y pairs within the set os points received.
    const mappedPoints = points.reduce(
      (acc: any, cur) => ({
        ...acc,
        [`valX${Math.abs(cur.x)}Y${Math.abs(cur.y)}`]: cur,
      }),
      {}
    );

    for (const point of Object.keys(mappedPoints)) {
      const formattedPoint = point as keyof typeof mappedPoints;
      if (!mappedPoints[formattedPoint]?.completed)
        mappedPoints[formattedPoint].decibelValue = undefined;
    }

    // Values in which Y = 1 (in the original X and Y position values).
    array[0][0] = mappedPoints.valX1Y1?.decibelValue;
    array[0][1] = mappedPoints.valX1Y1?.decibelValue;
    array[1][0] = mappedPoints.valX1Y1?.decibelValue;
    array[1][1] = mappedPoints.valX1Y1?.decibelValue;

    array[0][4] = mappedPoints.valX2Y1?.decibelValue;
    array[1][4] = mappedPoints.valX2Y1?.decibelValue;

    array[0][7] = mappedPoints.valX3Y1?.decibelValue;
    array[1][7] = mappedPoints.valX3Y1?.decibelValue;

    array[0][10] = mappedPoints.valX4Y1?.decibelValue;
    array[1][10] = mappedPoints.valX4Y1?.decibelValue;

    array[0][13] = mappedPoints.valX5Y1?.decibelValue;
    array[1][13] = mappedPoints.valX5Y1?.decibelValue;

    // Values in which Y = 2 (in the original X and Y position values).
    array[4][0] = mappedPoints.valX1Y2?.decibelValue;
    array[4][1] = mappedPoints.valX1Y2?.decibelValue;

    array[4][4] = mappedPoints.valX2Y2?.decibelValue;

    array[4][7] = mappedPoints.valX3Y2?.decibelValue;

    array[4][10] = mappedPoints.valX4Y2?.decibelValue;

    // For the 10-2 type, there's no value for this position (like we have on the 30-2, which is the one similar to 10-2).
    // This way, we represent it's value based on it's neighbours.
    const valX5Y2 =
      0.25 * mappedPoints.valX4Y3?.decibelValue +
      0.25 * mappedPoints.valX4Y2?.decibelValue +
      0.25 * mappedPoints.valX4Y1?.decibelValue +
      0.25 * mappedPoints.valX5Y1?.decibelValue;
    array[4][13] = valX5Y2;

    // Values in which Y = 3 (in the original X and Y position values).
    array[7][0] = mappedPoints.valX1Y3?.decibelValue;
    array[7][1] = mappedPoints.valX1Y3?.decibelValue;

    array[7][4] = mappedPoints.valX2Y3?.decibelValue;

    array[7][8] = mappedPoints.valX3Y3?.decibelValue;

    array[7][11] = mappedPoints.valX4Y3?.decibelValue;

    // Values in which Y = 4 (in the original X and Y position values).
    array[10][0] = mappedPoints.valX1Y4?.decibelValue;
    array[10][1] = mappedPoints.valX1Y4?.decibelValue;

    array[10][4] = mappedPoints.valX2Y4?.decibelValue;

    array[10][8] = mappedPoints.valX3Y4?.decibelValue;

    // Values in which Y = 5 (in the original X and Y position values).
    array[13][0] = mappedPoints.valX1Y5?.decibelValue;
    array[13][1] = mappedPoints.valX1Y5?.decibelValue;

    // For the 10-2 type, there's no value for this position (like we have on the 30-2, which is the one similar to 10-2).
    // This way, we represent it's value based on it's neighbours.
    const valX2Y5 =
      0.25 * mappedPoints.valX1Y4?.decibelValue +
      0.25 * mappedPoints.valX2Y4?.decibelValue +
      0.25 * mappedPoints.valX3Y4?.decibelValue +
      0.25 * mappedPoints.valX1Y5?.decibelValue;
    array[13][4] = valX2Y5;

    return array;
  };

  // Inject the final values into the array of arrays (this varies per exam type).
  const injectFinalValues = (array: Array<Array<number>>) => {
    // Method that will "merge" the initial values with their horizontal undefined values.
    const mergeInitialValuesHorizontally = () => {
      // Values in which Y = 0 or Y = 1.
      array[0][2] = 0.7 * array[0][1] + 0.3 * array[0][4];
      array[1][2] = 0.7 * array[1][1] + 0.3 * array[1][4];
      array[0][3] = 0.3 * array[0][1] + 0.7 * array[0][4];
      array[1][3] = 0.3 * array[1][1] + 0.7 * array[1][4];

      array[0][5] = 0.7 * array[0][4] + 0.3 * array[0][7];
      array[1][5] = 0.7 * array[1][4] + 0.3 * array[1][7];
      array[0][6] = 0.3 * array[0][4] + 0.7 * array[0][7];
      array[1][6] = 0.3 * array[1][4] + 0.7 * array[1][7];

      array[0][8] = 0.7 * array[0][7] + 0.3 * array[0][10];
      array[1][8] = 0.7 * array[1][7] + 0.3 * array[1][10];
      array[0][9] = 0.3 * array[0][7] + 0.7 * array[0][10];
      array[1][9] = 0.3 * array[1][7] + 0.7 * array[1][10];

      array[0][11] = 0.7 * array[0][10] + 0.3 * array[0][13];
      array[1][11] = 0.7 * array[1][10] + 0.3 * array[1][13];
      array[0][12] = 0.3 * array[0][10] + 0.7 * array[0][13];
      array[1][12] = 0.3 * array[1][10] + 0.7 * array[1][13];

      // Values in which Y = 4.
      array[4][2] = 0.7 * array[4][1] + 0.3 * array[4][4];
      array[4][3] = 0.3 * array[4][1] + 0.7 * array[4][4];

      array[4][5] = 0.7 * array[4][4] + 0.3 * array[4][7];
      array[4][6] = 0.3 * array[4][1] + 0.7 * array[4][4];

      array[4][8] = 0.7 * array[4][7] + 0.3 * array[4][10];
      array[4][9] = 0.3 * array[4][7] + 0.7 * array[4][10];

      array[4][11] = 0.7 * array[4][10] + 0.3 * array[4][13];
      array[4][12] = 0.3 * array[4][10] + 0.7 * array[4][13];

      // Values in which Y = 7.
      array[7][2] = 0.7 * array[7][1] + 0.3 * array[7][4];
      array[7][3] = 0.3 * array[7][1] + 0.7 * array[7][4];

      array[7][5] = 0.7 * array[7][4] + 0.3 * array[7][8];
      array[7][6] = 0.5 * array[7][4] + 0.5 * array[7][8];
      array[7][7] = 0.3 * array[7][4] + 0.7 * array[7][8];

      array[7][9] = 0.7 * array[7][8] + 0.3 * array[7][11];
      array[7][10] = 0.3 * array[7][8] + 0.7 * array[7][11];

      // Values in which Y = 10.
      array[10][2] = 0.7 * array[10][1] + 0.3 * array[10][4];
      array[10][3] = 0.3 * array[10][1] + 0.7 * array[10][4];

      array[10][5] = 0.7 * array[10][4] + 0.3 * array[10][8];
      array[10][6] = 0.5 * array[10][4] + 0.5 * array[10][8];
      array[10][7] = 0.3 * array[10][4] + 0.7 * array[10][8];

      // Values in which Y = 13.
      array[13][2] = 0.7 * array[13][1] + 0.3 * array[13][4];
      array[13][3] = 0.3 * array[13][1] + 0.7 * array[13][4];
    };

    // Method that will "merge" the initial values on their vertical undefined values.
    const mergeInitialValuesVertically = () => {
      // Values in which X = 0 or X = 1.
      array[2][0] = 0.7 * array[1][0] + 0.3 * array[4][0];
      array[2][1] = 0.7 * array[1][1] + 0.3 * array[4][1];
      array[3][0] = 0.3 * array[1][0] + 0.7 * array[4][0];
      array[3][1] = 0.3 * array[1][1] + 0.7 * array[4][1];

      array[5][0] = 0.7 * array[4][0] + 0.3 * array[7][0];
      array[5][1] = 0.7 * array[4][1] + 0.3 * array[7][1];
      array[6][0] = 0.3 * array[4][0] + 0.7 * array[7][0];
      array[6][1] = 0.3 * array[4][1] + 0.7 * array[7][1];

      array[8][0] = 0.7 * array[7][0] + 0.3 * array[10][0];
      array[8][1] = 0.7 * array[7][1] + 0.3 * array[10][1];
      array[9][0] = 0.3 * array[7][0] + 0.7 * array[10][0];
      array[9][1] = 0.3 * array[7][1] + 0.7 * array[10][1];

      array[11][0] = 0.7 * array[10][0] + 0.3 * array[13][0];
      array[11][1] = 0.7 * array[10][1] + 0.3 * array[13][1];
      array[12][0] = 0.3 * array[10][0] + 0.7 * array[13][0];
      array[12][1] = 0.3 * array[10][1] + 0.7 * array[13][1];

      // Values in which X = 4.
      array[2][4] = 0.7 * array[1][4] + 0.3 * array[4][4];
      array[3][4] = 0.3 * array[1][4] + 0.7 * array[4][4];

      array[5][4] = 0.7 * array[4][4] + 0.3 * array[7][4];
      array[6][4] = 0.3 * array[4][4] + 0.7 * array[7][4];

      array[8][4] = 0.7 * array[7][4] + 0.3 * array[10][4];
      array[9][4] = 0.3 * array[7][4] + 0.7 * array[10][4];

      array[11][4] = 0.7 * array[10][4] + 0.3 * array[13][4];
      array[12][4] = 0.3 * array[10][4] + 0.7 * array[13][4];

      // Values in which X = 7.
      array[2][7] = 0.7 * array[1][7] + 0.3 * array[4][7];
      array[3][7] = 0.3 * array[1][7] + 0.7 * array[4][7];

      // Values in which X = 8.
      array[8][8] = 0.7 * array[7][8] + 0.3 * array[10][8];
      array[9][8] = 0.3 * array[7][8] + 0.7 * array[10][8];

      // Values in which X = 10.
      array[2][10] = 0.7 * array[1][10] + 0.3 * array[4][10];
      array[3][10] = 0.3 * array[1][10] + 0.7 * array[4][10];

      // Values in which X = 13.
      array[2][13] = 0.7 * array[1][13] + 0.3 * array[4][13];
      array[3][13] = 0.3 * array[1][13] + 0.7 * array[4][13];
    };

    // Fill the inner values that are not yet defined. This depends on initialGridDefinition, injectInitialValues, mergeInitialValuesHorizontally and mergeInitialValuesVertically.
    // Method that will "merge"/"blend" the inner gap values, to be a representation of its neighbours (top-bottom-left).
    const mergeInnerGaps = () => {
      // Values in which Y = 2 or Y = 3.
      array[2][2] = 0.7 * array[1][2] + 0.3 * array[4][2];
      array[2][3] = 0.7 * array[1][3] + 0.3 * array[4][3];
      array[3][2] = 0.3 * array[1][2] + 0.7 * array[4][2];
      array[3][3] = 0.3 * array[1][3] + 0.7 * array[4][3];

      array[2][5] = 0.7 * array[1][5] + 0.3 * array[4][5];
      array[2][6] = 0.7 * array[1][6] + 0.3 * array[4][6];
      array[3][5] = 0.3 * array[1][5] + 0.7 * array[4][5];
      array[3][6] = 0.3 * array[1][6] + 0.7 * array[4][6];

      array[2][8] = 0.7 * array[1][8] + 0.3 * array[4][8];
      array[2][9] = 0.7 * array[1][9] + 0.3 * array[4][9];
      array[3][8] = 0.3 * array[1][8] + 0.7 * array[4][8];
      array[3][9] = 0.3 * array[1][9] + 0.7 * array[4][9];

      array[2][11] = 0.7 * array[1][11] + 0.3 * array[4][11];
      array[2][12] = 0.7 * array[1][12] + 0.3 * array[4][12];
      array[3][11] = 0.3 * array[1][11] + 0.7 * array[4][11];
      array[3][12] = 0.3 * array[1][12] + 0.7 * array[4][12];

      // // Values in which Y = 5 or Y = 6.
      array[5][2] = 0.7 * array[4][2] + 0.3 * array[7][2];
      array[5][3] = 0.7 * array[4][3] + 0.3 * array[7][3];
      array[6][2] = 0.3 * array[4][2] + 0.7 * array[7][2];
      array[6][3] = 0.3 * array[4][3] + 0.7 * array[7][3];

      array[5][5] = 0.7 * array[4][5] + 0.3 * array[7][5];
      array[5][6] = 0.7 * array[4][6] + 0.3 * array[7][6];
      array[5][7] = 0.7 * array[4][7] + 0.3 * array[7][7];
      array[5][8] = 0.7 * array[4][8] + 0.3 * array[7][8];
      array[5][9] = 0.7 * array[4][9] + 0.3 * array[7][9];
      array[5][10] = 0.7 * array[4][10] + 0.3 * array[7][10];
      array[5][11] = 0.7 * array[4][11] + 0.3 * array[7][11];

      array[6][5] = 0.3 * array[4][5] + 0.7 * array[7][5];
      array[6][6] = 0.3 * array[4][6] + 0.7 * array[7][6];
      array[6][7] = 0.3 * array[4][7] + 0.7 * array[7][7];
      array[6][8] = 0.3 * array[4][8] + 0.7 * array[7][8];
      array[6][9] = 0.3 * array[4][9] + 0.7 * array[7][9];
      array[6][10] = 0.3 * array[4][10] + 0.7 * array[7][10];
      array[6][11] = 0.3 * array[4][11] + 0.7 * array[7][11];

      // Values in which Y = 8 or Y = 9.
      array[8][2] = 0.7 * array[7][2] + 0.3 * array[10][2];
      array[8][3] = 0.7 * array[7][3] + 0.3 * array[10][3];
      array[9][2] = 0.3 * array[7][2] + 0.7 * array[10][2];
      array[9][3] = 0.3 * array[7][3] + 0.7 * array[10][3];

      array[8][5] = 0.7 * array[7][5] + 0.3 * array[10][5];
      array[8][6] = 0.7 * array[7][6] + 0.3 * array[10][6];
      array[8][7] = 0.7 * array[7][7] + 0.3 * array[10][7];
      array[9][5] = 0.3 * array[7][5] + 0.7 * array[10][5];
      array[9][6] = 0.3 * array[7][6] + 0.7 * array[10][6];
      array[9][7] = 0.3 * array[7][7] + 0.7 * array[10][7];

      array[8][9] = 0.7 * array[7][9] + 0.3 * array[10][9];
      array[9][9] = 0.3 * array[7][9] + 0.7 * array[10][9];

      // // Values in which Y = 11 or Y = 12.
      array[11][2] = 0.7 * array[10][2] + 0.3 * array[13][2];
      array[11][3] = 0.7 * array[10][3] + 0.3 * array[13][3];
      array[12][2] = 0.3 * array[10][2] + 0.7 * array[13][2];
      array[12][3] = 0.3 * array[10][3] + 0.7 * array[13][3];

      array[11][5] = array[11][4];
      array[12][5] = array[12][4];

      array[11][6] = array[11][5];
      array[12][6] = array[12][5];

      array[11][7] = array[10][7];
      array[11][8] = array[10][8];

      // Values in which Y = 10.
      array[10][9] = array[10][8];

      // Values in which Y = 8 or Y = 9.
      array[9][9] = 0.7 * array[10][9] + 0.3 * array[7][9];
      array[8][9] = 0.3 * array[10][9] + 0.7 * array[7][9];

      array[9][10] = array[9][9];
      array[8][10] = array[8][9];

      array[8][11] = array[7][11];

      // Values in which Y = 5 or Y = 6.
      array[6][12] = array[6][11];
      array[5][12] = array[5][11];
    };

    // Update the values on the array (grid).
    mergeInitialValuesHorizontally();
    mergeInitialValuesVertically();
    mergeInnerGaps();

    return array;
  };

  // We first build a grid filled with undefined values.
  const rawGrid = initialGridDefinition(gridSize);
  // We then inject the initial values, based on the decibel values for the given quadrant points.
  const initialGrid = injectInitialValues(rawGrid, points);
  // We then inject the final values, which are besed on the definition of the initial values.
  const finalGrid = injectFinalValues(initialGrid);

  // We reverse it, so the grid system used to build it matches the axis point definitions on the pressedPoints.
  // For example: the 1,1 position on the grid would be place on a row at the top of the grid; but the 1,1 position on the axis world would coordinate the bottom; so we reverse the grid, so that it matches the axis world when displayed.
  return finalGrid.reverse();
};

/**
 * Checks if the section grid should be extended or not.
 * @param {*} sectionSide The side of the quadrant section in relation to the chart cross.
 * @param {*} eyeType The side of the eye.
 * @returns A boolean informing whether the grid should be extended or not.
 */
export const shouldExtendGrid = (sectionSide: string, eyeType: string) => {
  const { LEFT, RIGHT } = eyeTypeEnums;

  // If eye is right, extend left section
  if (eyeType === RIGHT && sectionSide === LEFT) return true;

  // If eye is left, extend right section
  if (eyeType === LEFT && sectionSide === RIGHT) return true;

  return false;
};

/**
 * Method to build the quadrant grid based on the grid type, and which eye the report refers to.
 * @param {*} gridType The grid type of the exam (currently we have 30-2, 24-2 or 10-2).
 * @param {*} sectionSide The grid section side (based on the X and Y values in comparison to the inner cross of a the charts). E.g.: the top left quadrant would have "Left" as value, while the top right quadrant should have "Right" as value.
 * @param {*} eyeType The eye in which the report refers to ("Left" or "Right").
 * @param {*} points The values.
 * @returns An array representing the grid for a quadrant.
 */
export const buildQuadrantGrid = ({
  gridType,
  sectionSide,
  eyeType,
  points = [],
}: {
  gridType: string | undefined;
  sectionSide: string;
  eyeType: string;
  points: Array<ChartSpotType>;
}) => {
  // Checks if the grid for this section should be extended (can only be extended when the exam type is 24-2).
  const shouldExtend =
    gridType === gridTypeEnums.G_24_2.type || gridTypeEnums.G_24_2_C.type
      ? shouldExtendGrid(sectionSide, eyeType)
      : false;

  let grid: Array<Array<number>> = [];
  if (gridType === gridTypeEnums.G_24_2.type)
    grid = build24Dash2Grid(points, shouldExtend);
  else if (gridType === gridTypeEnums.G_24_2_C.type)
    grid = build24Dash2Grid(points, shouldExtend);
  else if (gridType === gridTypeEnums.G_30_2.type)
    grid = build30Dash2Grid(points);
  else if (gridType === gridTypeEnums.G_10_2.type)
    grid = build10Dash2Grid(points);

  return grid;
};

/**
 * Adding the decibel values for each neighbour, so we can process the image based on the neighbours' values
 * @param {*} pressedPoints The points that we will calculate the neighbours for.
 * @returns The pressedPoints with each point containing the neighbours information.
 */
export const calculateNeighbours = (pressedPoints: Array<ChartSpotType>) =>
  pressedPoints.map((point) => {
    // method to add an extra value to help reaching the neighbours from a point on the grid.
    // when on x = 1 and y = 1, the neighbour to the left for example, should be the item on x = -1 and y = 1 (not x = 0 and y = 1).
    const xOffset = (curX: number, targetX: number) => {
      // is on the right of the chart
      if (curX > 0) {
        return targetX === 0 ? -1 : targetX;
      }

      // is on the left of the chart
      return targetX === 0 ? 1 : targetX;
    };

    // method to add an extra value to help reaching the neighbours from a point on the grid.
    // when on x = 1 and y = 1, the neighbour to the bottom for example, should be the item on x = 1 and y = -1 (not x = 1 and y = 0).
    const yOffset = (curY: number, targetY: number) => {
      // is on the top of the chart
      if (curY > 0) {
        return targetY === 0 ? -1 : targetY;
      }

      // is on the bottom of the chart
      return targetY === 0 ? 1 : targetY;
    };

    return {
      ...point,
      neighboursDecibel: {
        topLeft: pressedPoints.find(
          (e) =>
            e.x === xOffset(point.x, point.x - 1) &&
            e.y === yOffset(point.y, point.y + 1)
        )?.decibelValue,
        top: pressedPoints.find(
          (e) =>
            e.x === xOffset(point.x, point.x) &&
            e.y === yOffset(point.y, point.y + 1)
        )?.decibelValue,
        topRight: pressedPoints.find(
          (e) =>
            e.x === xOffset(point.x, point.x + 1) &&
            e.y === yOffset(point.y, point.y + 1)
        )?.decibelValue,
        right: pressedPoints.find(
          (e) =>
            e.x === xOffset(point.x, point.x + 1) &&
            e.y === yOffset(point.y, point.y)
        )?.decibelValue,
        bottomRight: pressedPoints.find(
          (e) =>
            e.x === xOffset(point.x, point.x + 1) &&
            e.y === yOffset(point.y, point.y - 1)
        )?.decibelValue,
        bottom: pressedPoints.find(
          (e) =>
            e.x === xOffset(point.x, point.x) &&
            e.y === yOffset(point.y, point.y - 1)
        )?.decibelValue,
        bottomLeft: pressedPoints.find(
          (e) =>
            e.x === xOffset(point.x, point.x - 1) &&
            e.y === yOffset(point.y, point.y - 1)
        )?.decibelValue,
        left: pressedPoints.find(
          (e) =>
            e.x === xOffset(point.x, point.x - 1) &&
            e.y === yOffset(point.y, point.y)
        )?.decibelValue,
      },
    };
  });

//  gives pattern image for the decibel values, and the alt text for the image to be displayed.
export const getImageInfo = (val: number) => {
  if (val <= 0)
    return {
      imageSrc: Grayscale0,
      alt: "Grayscale 0 value representation.",
    };
  else if (val <= 5 && val > 0)
    return {
      imageSrc: Grayscale1to5,
      alt: "Grayscale 1 to 5 value representation.",
    };
  else if (val <= 10 && val > 5)
    return {
      imageSrc: Grayscale6to10,
      alt: "Grayscale 6 to 10 value representation.",
    };
  else if (val <= 15 && val > 10)
    return {
      imageSrc: Grayscale11to15,
      alt: "Grayscale 11 to 15 value representation.",
    };
  else if (val <= 20 && val > 15)
    return {
      imageSrc: Grayscale16to20,
      alt: "Grayscale 16 to 20 value representation.",
    };
  else if (val <= 25 && val > 20)
    return {
      imageSrc: Grayscale21to25,
      alt: "Grayscale 21 to 25 value representation.",
    };
  else if (val <= 30 && val > 25)
    return {
      imageSrc: Grayscale26to30,
      alt: "Grayscale 26 to 30 value representation.",
    };
  else if (val <= 35 && val > 30)
    return {
      imageSrc: Grayscale31to35,
      alt: "Grayscale 31 to 35 value representation.",
    };
  else if (val <= 40 && val > 35)
    return {
      imageSrc: Grayscale36to40,
      alt: "Grayscale 36 to 40 value representation.",
    };
  else if (val <= 50 && val > 40)
    return {
      imageSrc: Grayscale41to50,
      alt: "Grayscale 41 to 50 value representation.",
    };

  return {
    imageSrc: Undefined,
    alt: "Undefined grayscale value representation.",
  };
};

//  gives pattern image for the total deviation and pattern deviation charts
export const getDeviationChartPattern = (val: number | undefined) => {
  if (val === undefined) return undefined;

  if (val < 0.5) return LessThan05;
  else if (val < 1) return LessThan1;
  else if (val < 2) return LessThan2;
  else if (val < 5) return LessThan5;
  return undefined;
};

export const getTotalChartSpots = (examReport: ExamSectionReportType) => {
  if (!examReport?.postProcessing) return [];
  if (!examReport?.postProcessing?.processedPoints) return [];
  if (examReport?.state?.visualSpots?.length === 0) return [];
  if (!examReport?.postProcessing?.processedPoints.length) return [];

  // formatting the spots into another structure, used by the total chart
  const formattedTotalSpots = examReport?.postProcessing.processedPoints?.map(
    (point) => ({
      decibelValue: point.TotalDeviation,
      valuePercentile: isNaN(point?.TotalDeviationPercentile)
        ? undefined
        : point.TotalDeviationPercentile * 100,
      x: point?.Spot.position.x,
      y: point?.Spot.position.y,
      completed: point?.Spot.isCompleted,
      current: false,
    })
  );

  return formattedTotalSpots;
};

export const getPatternDevChartSpots = (examReport: ExamSectionReportType) => {
  if (!examReport?.postProcessing) return [];
  if (!examReport?.postProcessing?.processedPoints) return [];
  if (examReport?.state?.visualSpots?.length === 0) return [];
  if (!examReport?.postProcessing?.processedPoints.length) return [];

  // formatting the spots into another structure, used by the pattern dev chart
  const formattedPatternDevSpots =
    examReport?.postProcessing.processedPoints?.map((point) => ({
      decibelValue: point.PatternDeviation,
      valuePercentile: isNaN(point.PatternDeviationPercentile)
        ? undefined
        : point.PatternDeviationPercentile * 100,
      x: point?.Spot.position.x,
      y: point?.Spot.position.y,
      completed: point?.Spot.isCompleted,
      current: false,
    }));

  return formattedPatternDevSpots;
};

type ProgressionChartInputType =
  | EnrichedRetinaLogikSectionType
  | EnrichedExternalExamType
  | SelectedProgressionExamType;

export const formatProgressionAnalysisData = (
  inputExams: Array<ProgressionChartInputType>,
  dateOfBirth: string | undefined,
  clinicTimezone: string | null
) => {
  return inputExams.map((exam: ProgressionChartInputType) => {
    // Picker rows carry the completion date at the top level; rows coming back
    // from a saved analysis still nest it under `data`.
    const externalData = "data" in exam ? exam.data : undefined;
    const reportData = "reportData" in exam ? exam.reportData : undefined;

    const completionDate =
      ("completionDate" in exam ? exam.completionDate : undefined) ??
      externalData?.completionDate;

    // get MD from external or Retinalogik exam
    const mdValue = externalData
      ? getDecimalNum(externalData.meanDeviation, 1)
      : reportData?.postProcessing?.totalMeanDeviation !== undefined
        ? getDecimalNum(reportData?.postProcessing?.totalMeanDeviation, 1)
        : null;

    // get PSD from external or Retinalogik exam
    const psdValue = externalData
      ? getDecimalNum(externalData.patternStandardDeviation, 1)
      : reportData?.postProcessing?.patternStandardDeviation !== undefined
        ? getDecimalNum(reportData?.postProcessing?.patternStandardDeviation, 1)
        : null;

    // get VRFI from external or Retinalogik exam; both are 0-1 fractions,
    // charted as 0-100
    const retinaLogikFieldIndex =
      reportData?.postProcessing?.retinaLogikFieldIndex;
    // left out (null) when it's N/A rather than charted as 0
    const vrfiValue = externalData
      ? externalData.visualFieldIndex * 100
      : typeof retinaLogikFieldIndex === "number" &&
          !isVrfiNotApplicable(
            reportData?.postProcessing?.totalMeanDeviation,
            retinaLogikFieldIndex
          )
        ? retinaLogikFieldIndex * 100
        : null;

    const algorithmValue = externalData
      ? externalData.algorithmType
      : reportData?.exam.algorithm;

    return {
      age: calculateAgeFromExamDate(dateOfBirth ?? "", completionDate ?? ""),
      time: completionDate ? getLocalDate(completionDate, clinicTimezone) : "",
      MD: mdValue,
      PSD: psdValue,
      VRFI: vrfiValue,
      pointShape:
        PROGRESSION_CHART_SHAPES[
          algorithmValue as keyof typeof PROGRESSION_CHART_SHAPES
        ],
      isRetinaLogikExam:
        "source" in exam
          ? exam.source === examSourceEnums.RETINALOGIK
          : exam.isRetinaLogikExam,
    };
  });
};

export const getElapsedTime = (elapsedTime: number) => {
  const minutes = Math.floor(elapsedTime / 60);
  const remaining_seconds = Math.floor(elapsedTime % 60);
  const formatted_time =
    String(minutes).padStart(2, "0") +
    ":" +
    String(remaining_seconds).padStart(2, "0");
  return formatted_time;
};

export const canEditExamSection = (
  clinicLicensing: ClinicType["licensing"],
  exam: ExamType | ClinicPackageExamType
) => {
  const isMultiPackageDisabled =
    !clinicLicensing?.canUseExamPackaging && isMultiExamPackage(exam);
  const isSynoptophoreDisabled =
    !clinicLicensing?.canUseSynoptophoreExams &&
    ((exam?.deviationAndCorrespondenceSections &&
      exam?.deviationAndCorrespondenceSections?.length > 0) ||
      (exam?.fusionAndVergenceSections &&
        exam?.fusionAndVergenceSections?.length > 0) ||
      (exam?.stereopsisSections && exam?.stereopsisSections?.length > 0));
  const isAdvancedEyeTrackingDisabled =
    !clinicLicensing?.canUseAdvancedEyeTracking &&
    exam?.deviationAndCorrespondenceSections &&
    exam?.deviationAndCorrespondenceSections?.length > 0;
  const isRAPDDisabled =
    !clinicLicensing?.canUseRAPDExams &&
    exam?.rapdSections &&
    exam?.rapdSections?.length > 0;
  const isAniseikoniaDisabled =
    !clinicLicensing?.canUseAniseikoniaExams &&
    exam?.aniseikoniaSections &&
    exam?.aniseikoniaSections?.length > 0;
  const isOcularMotilityDisabled =
    !clinicLicensing?.canUseOcularMotilityExams &&
    exam?.ocularMotilitySections &&
    exam?.ocularMotilitySections?.length > 0;
  const isIOLSimDisabled =
    !clinicLicensing?.canUseIOLSimulations &&
    exam?.iolLensSimulationSections &&
    exam?.iolLensSimulationSections?.length > 0;
  const isProgressiveSimDisabled =
    !clinicLicensing?.canUseProgressiveSimulations &&
    exam?.progressiveLensSimulationSections &&
    exam?.progressiveLensSimulationSections?.length > 0;
  const isDryEyeSimDisabled =
    !clinicLicensing?.canUseDryEyeSimulations &&
    exam?.dryEyeSimulationSections &&
    exam?.dryEyeSimulationSections?.length > 0;

  return (
    isMultiPackageDisabled ||
    isSynoptophoreDisabled ||
    isAdvancedEyeTrackingDisabled ||
    isRAPDDisabled ||
    isOcularMotilityDisabled ||
    isAniseikoniaDisabled ||
    isIOLSimDisabled ||
    isProgressiveSimDisabled ||
    isDryEyeSimDisabled
  );
};

// Nine-gaze and H-pattern both name their positions after the same compass
// directions (H-pattern just never visits the pure Up/Down ones), prefixed
// with which exam produced them -- strip whichever prefix is actually there.
const OCULAR_MOTILITY_POSITION_PREFIXES = ["Nine_Gaze_", "H_Pattern_"];

export const getOcularMotilityPositionKey = (focalPointPosition: string) => {
  const prefix = OCULAR_MOTILITY_POSITION_PREFIXES.find((candidate) =>
    focalPointPosition.startsWith(candidate)
  );
  return prefix ? focalPointPosition.slice(prefix.length) : focalPointPosition;
};

export const getOcularMotilityPositionLabel = (
  positionLayout: Array<OcularMotilityPositionLayoutType>,
  focalPointPosition: string
) =>
  positionLayout.find(
    (position) =>
      position.key === getOcularMotilityPositionKey(focalPointPosition)
  )?.label ?? focalPointPosition;
